import { DeleteOutlined, DownOutlined, RobotOutlined } from '@ant-design/icons';
import { DrawerForm, PageContainer, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Dropdown, Space, Typography } from 'antd';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import HotPotPreview, { renderHotPotSourceLink } from '../components/HotPotPreview';
import {
  buildAgentChatEntryPath,
  canStartAgentChatEntry,
  getAgentChatEntryButtonText,
} from '../utils/agent-chat-entry';
import {
  getIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
} from '../services/ip-creator';

const resource = 'hot-pots';
const resourceName = '热点采集';

const statusValueEnum = {
  已淘汰: { text: '已淘汰', status: 'Error' },
  已入选: { text: '已入选', status: 'Success' },
};

export const ellipsisHotTitle = (text) => {
  if (!text) {
    return '-';
  }
  const normalizedText = String(text);
  return normalizedText.length > 20 ? `${normalizedText.slice(0, 20)}...` : normalizedText;
};

export const renderHotTitle = (_, record) => (
  <Typography.Text title={record.title}>{ellipsisHotTitle(record.title)}</Typography.Text>
);

const HotPots = () => {
  const actionRef = useRef(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const navigate = useNavigate();
  const { organizationId, resolveOrganizationPath } = useOrganizationPath();
  const { message, modal } = App.useApp();

  const reload = () => actionRef.current?.reload?.();

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: (payload) => getIpCreatorRecord(resource, payload),
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const removeRecordWithConfirm = (record) => {
    modal.confirm({
      title: `移除${resourceName}`,
      content: `确认移除 ${record.title || record.id}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        removeRecord({ data: { id: record.id, organizationId } });
        setDetailRecord(null);
      },
    });
  };

  const handleMoreAction = (key, record) => {
    if (key === 'remove') {
      removeRecordWithConfirm(record);
    }
  };

  const openAgentChatEntry = (record) => {
    navigate(resolveOrganizationPath(buildAgentChatEntryPath({ record, seedType: 'hotPot' })));
  };

  const columns = [
    {
      title: '热点标题',
      dataIndex: 'title',
      ellipsis: true,
      fixed: 'left',
      order: 100,
      render: renderHotTitle,
      search: true,
      width: 220,
    },
    { title: '热度指数', dataIndex: 'hot', ellipsis: true, search: false },
    {
      title: '源头链接',
      dataIndex: 'url',
      search: false,
      render: renderHotPotSourceLink,
    },
    { title: 'IP 契合度', dataIndex: 'relevance', ellipsis: true, search: false },
    {
      title: '采集状态',
      dataIndex: 'status',
      ellipsis: true,
      order: 70,
      search: true,
      valueEnum: statusValueEnum,
      valueType: 'select',
    },
    { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
    {
      title: '操作',
      valueType: 'option',
      render: (_, record) => [
        <Button
          key="detail"
          type="link"
          onClick={() => getRecord({ data: { id: record.id, organizationId } })}
        >
          详情
        </Button>,
        <Dropdown
          key="more"
          menu={{
            onClick: ({ key }) => handleMoreAction(key, record),
            items: [
              {
                key: 'remove',
                danger: true,
                disabled: removing,
                label: '移除',
              },
            ],
          }}
        >
          <Button type="link">
            更多 <DownOutlined />
          </Button>
        </Dropdown>,
      ],
    },
  ];

  return (
    <PageContainer content="采集并维护公域热点线索">
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle={resourceName}
        request={(params) => queryIpCreatorRecords(resource, { ...params, organizationId })}
        columns={columns}
        search={{ labelWidth: 96 }}
        scroll={{ x: 'max-content' }}
      />
      <DrawerForm
        title={`${resourceName}详情`}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRecord(null);
          }
        }}
        submitter={false}
        width={1024}
        drawerProps={{
          extra: canStartAgentChatEntry({ record: detailRecord, seedType: 'hotPot' }) ? (
            <Space>
              <Button icon={<RobotOutlined />} onClick={() => openAgentChatEntry(detailRecord)}>
                {getAgentChatEntryButtonText(detailRecord)}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={removing}
                onClick={() => removeRecordWithConfirm(detailRecord)}
              >
                删除
              </Button>
            </Space>
          ) : null,
        }}
      >
        <HotPotPreview record={detailRecord} />
      </DrawerForm>
    </PageContainer>
  );
};

export default HotPots;
